import React from 'react';
import {
  Box, Card, Typography, Avatar, Button, TextField, useTheme, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, Alert, IconButton, useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyIcon from '@mui/icons-material/Key';
import MapIcon from '@mui/icons-material/Map';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { fetchCities, fetchUserById } from './api';
import { changePassword } from './usersApi';
import { useCart } from './CartContext';
import config from './config';
import { useTranslation } from 'react-i18next';

function UserProfile() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { clearCart } = useCart();
  const { t, i18n } = useTranslation();

  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saveMsg, setSaveMsg] = React.useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isChangingPassword, setIsChangingPassword] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState('');

  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);

  // ✅ NEW: City change state
  const [cities, setCities] = React.useState([]);
  const [cityDialogOpen, setCityDialogOpen] = React.useState(false);
  const [savingCity, setSavingCity] = React.useState(false);

  React.useEffect(() => {
    const localUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

    if (localUser && (localUser.Id || localUser.id)) {
      Promise.all([
        fetchUserById(localUser.Id || localUser.id),
        fetchCities()
      ]).then(([fetchedUser, citiesData]) => {
        if (fetchedUser) { setUser(fetchedUser); localStorage.setItem('user', JSON.stringify(fetchedUser)); }
        else { setUser(null); }
        setCities(citiesData || []);
      }).catch(() => setUser(null)).finally(() => setLoading(false));
    } else {
      setUser(null);
      setLoading(false);
    }
  }, []);

  const handlePasswordChangeSubmit = async () => {
    setPasswordError('');
    if (!oldPassword || !newPassword || !confirmPassword) { setPasswordError(t('userProfile.fillAllFields', 'All fields are required.')); return; }
    if (newPassword !== confirmPassword) { setPasswordError(t('userProfile.passwordsMismatch', 'New passwords do not match.')); return; }
    setIsChangingPassword(true);
    try {
      const userId = user?.Id || user?.id;
      if (!userId) return;
      const response = await changePassword(userId, { OldPassword: oldPassword, NewPassword: newPassword, ConfirmPassword: confirmPassword });
      if (response && response.success) {
        setSaveMsg(t('userProfile.passwordChanged', 'Password changed successfully.'));
        setTimeout(() => setSaveMsg(''), 3000);
        setPasswordDialogOpen(false);
        setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      } else { setPasswordError(response?.message || 'Failed to change password.'); }
    } catch (e) {
      setPasswordError(e?.response?.data?.message || e?.response?.data?.errors?.[0] || 'An error occurred while changing the password.');
    } finally { setIsChangingPassword(false); }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true); setDeleteError('');
    try {
      const userId = user.Id || user.id;
      const res = await fetch(`${config.API_BASE_URL}/UsersApi/${userId}`, { method: 'DELETE' });
      if (res.ok) { localStorage.removeItem('user'); localStorage.removeItem('token'); clearCart(); setDeleteDialogOpen(false); window.location.href = '/select-branch'; }
      else { setDeleteError(t('userProfile.deleteFailed', 'Failed to delete account.')); }
    } catch { setDeleteError(t('userProfile.deleteFailed', 'Failed to delete account.')); }
    setDeleting(false);
  };

  const handleLogout = () => { localStorage.removeItem('user'); localStorage.removeItem('token'); clearCart(); window.location.href = '/select-branch'; };

  // ✅ NEW: Handle city change (same logic as mobile)
  const handleChangeCity = async (city) => {
    if (!user) return;
    setSavingCity(true);
    setCityDialogOpen(false);
    try {
      const cityName = city.Name || city.name || '';
      const updatedUser = { ...user, City: cityName };
      const userId = user.Id || user.id;
      const res = await fetch(`${config.API_BASE_URL}/UsersApi/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedUser)
      });
      if (res.ok) {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        window.dispatchEvent(new Event('userChanged'));
        setSaveMsg(t('userProfile.saveSuccess', 'Saved successfully.'));
      } else { setSaveMsg(t('userProfile.saveFailed', 'Failed to save.')); }
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg(t('userProfile.saveFailed', 'Failed to save.'));
      setTimeout(() => setSaveMsg(''), 3000);
    } finally { setSavingCity(false); }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">{t('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: i18n.dir(), p: 3 }}>
        <Card sx={{ p: 5, borderRadius: '24px', boxShadow: 6, textAlign: 'center', maxWidth: 400, width: '100%' }}>
          <AccountCircleIcon sx={{ fontSize: 100, color: '#E5E7EB', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>{t('userProfile.noUserTitle', 'Not Signed In')}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            {t('userProfile.noUserSubtitle', 'Sign in to access your profile and manage your account')}
          </Typography>
          <Button variant="contained" color="primary" href="/login" sx={{ fontWeight: 'bold', borderRadius: '16px', px: 4, py: 1.5 }}>
            {t('userProfile.signIn', 'Sign In')}
          </Button>
        </Card>
      </Box>
    );
  }
    return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', bgcolor: '#F9FAFB', py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 }, direction: i18n.dir() }}>
      <Box sx={{ maxWidth: 560, width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Profile Header Card */}
        <Card sx={{ borderRadius: '20px', p: { xs: 2.5, sm: 3 }, boxShadow: `0 2px 12px ${alpha('#000', 0.08)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: { xs: 64, sm: 72 }, height: { xs: 64, sm: 72 }, fontSize: { xs: 28, sm: 32 }, fontWeight: 'bold' }}>
              {(user.FullName || user.fullName || 'U').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, color: '#111827' }}>
                {user.FullName || user.fullName || 'User'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 14, color: '#6B7280' }} />
                <Typography variant="body2" color="text.secondary">{user.Phone || user.phone || 'N/A'}</Typography>
              </Box>
              {(user.Email || user.email) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: '#6B7280' }} />
                  <Typography variant="body2" color="text.secondary">{user.Email || user.email}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Card>

        {/* Account Information Section */}
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, px: 0.5, fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#111827' }}>
            {t('userProfile.accountInfo', 'Account Information')}
          </Typography>
          <Card sx={{ borderRadius: '16px', boxShadow: `0 1px 8px ${alpha('#000', 0.06)}`, overflow: 'hidden' }}>
            {/* Full Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Avatar sx={{ bgcolor: '#F3F4F6', width: 40, height: 40, mr: 1.5 }}>
                <PersonIcon sx={{ color: '#000', fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>{t('userProfile.fullName', 'Full Name')}</Typography>
                <Typography variant="body1" fontWeight={600} color="#111827">{user.FullName || user.fullName || 'N/A'}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#F3F4F6', width: 28, height: 28 }}>
                <LockIcon sx={{ fontSize: 12, color: '#9CA3AF' }} />
              </Avatar>
            </Box>

            <Divider sx={{ mx: 2 }} />

            {/* Phone */}
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Avatar sx={{ bgcolor: '#F3F4F6', width: 40, height: 40, mr: 1.5 }}>
                <PhoneIcon sx={{ color: '#000', fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>{t('userProfile.mobileNumber', 'Mobile Number')}</Typography>
                <Typography variant="body1" fontWeight={600} color="#111827">{user.Phone || user.phone || 'N/A'}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#F3F4F6', width: 28, height: 28 }}>
                <LockIcon sx={{ fontSize: 12, color: '#9CA3AF' }} />
              </Avatar>
            </Box>

            <Divider sx={{ mx: 2 }} />

            {/* ✅ NEW: Editable City Row */}
            <Box onClick={() => !savingCity && setCityDialogOpen(true)} sx={{ display: 'flex', alignItems: 'center', p: 2, cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB' }, transition: 'background 0.2s' }}>
              <Avatar sx={{ bgcolor: '#F3F4F6', width: 40, height: 40, mr: 1.5 }}>
                <BusinessIcon sx={{ color: '#000', fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>{t('userProfile.city', 'City')}</Typography>
                <Typography variant="body1" fontWeight={600} color="#111827">
                  {savingCity ? t('common.saving', 'Saving...') : (user.City || user.city || t('common.notAvailable', 'N/A'))}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#E0F2FE', width: 28, height: 28 }}>
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#0EA5E9' }} />
              </Avatar>
            </Box>

            {/* Email */}
            {(user.Email || user.email) && (
              <>
                <Divider sx={{ mx: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                  <Avatar sx={{ bgcolor: '#F3F4F6', width: 40, height: 40, mr: 1.5 }}>
                    <EmailIcon sx={{ color: '#000', fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>{t('userProfile.email', 'Email')}</Typography>
                    <Typography variant="body1" fontWeight={600} color="#111827">{user.Email || user.email}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#F3F4F6', width: 28, height: 28 }}>
                    <LockIcon sx={{ fontSize: 12, color: '#9CA3AF' }} />
                  </Avatar>
                </Box>
              </>
            )}
          </Card>
        </Box>

        {/* Delivery Location Section */}
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, px: 0.5, fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#111827' }}>
            {t('userProfile.deliveryLocation', 'Delivery Location')}
          </Typography>
          <Card sx={{ borderRadius: '16px', boxShadow: `0 1px 8px ${alpha('#000', 0.06)}`, p: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: '#E0F2FE', width: 56, height: 56 }}>
                <LocationOnIcon sx={{ color: '#0EA5E9', fontSize: 24 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="bold" color="#111827">
                  {user.City || user.city || t('common.notAvailable', 'N/A')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {user.Address || user.address || t('common.notAvailable', 'N/A')}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Success/Error Toast */}
        {saveMsg && (
          <Alert
            severity={saveMsg.toLowerCase().includes('success') || saveMsg.toLowerCase().includes('نجاح') ? 'success' : 'error'}
            icon={saveMsg.toLowerCase().includes('success') || saveMsg.toLowerCase().includes('نجاح') ? <CheckCircleIcon /> : <ErrorIcon />}
            sx={{ borderRadius: '12px' }}
          >
            {saveMsg}
          </Alert>
        )}

        {/* Actions Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <Button variant="outlined" fullWidth onClick={() => { setPasswordError(''); setOldPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordDialogOpen(true); }} sx={{ py: 1.8, borderRadius: '16px', fontWeight: 'bold', fontSize: '0.95rem', borderColor: '#E5E7EB', color: '#111827', borderWidth: 1.5, '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' } }} startIcon={<LockOutlinedIcon />}>
            {t('userProfile.changePassword', 'Change Password')}
          </Button>
          <Button variant="contained" fullWidth onClick={() => setLogoutDialogOpen(true)} sx={{ py: 1.8, borderRadius: '16px', fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#FEF2F2', color: '#EF4444', border: '1px solid #FEE2E2', boxShadow: 'none', '&:hover': { bgcolor: '#FEE2E2', boxShadow: 'none' } }} startIcon={<LogoutIcon />}>
            {t('userProfile.logOut', 'Log Out')}
          </Button>
          <Button variant="outlined" fullWidth onClick={() => setDeleteDialogOpen(true)} sx={{ py: 1.8, borderRadius: '16px', fontWeight: 'bold', fontSize: '0.95rem', borderColor: '#FEE2E2', color: '#EF4444', borderWidth: 1.5, '&:hover': { borderColor: '#FCA5A5', bgcolor: '#FEF2F2' } }} startIcon={<DeleteOutlineIcon />}>
            {t('userProfile.deleteAccount', 'Delete Account')}
          </Button>
        </Box>
      </Box>

      {/* ─── Change Password Dialog ─── */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: { xs: 1, sm: 2 } } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold', fontSize: '1.25rem' }}>
          <Avatar sx={{ bgcolor: '#F3F4F6', width: 48, height: 48 }}>
            <KeyIcon sx={{ color: '#000', fontSize: 24 }} />
          </Avatar>
          {t('userProfile.changePassword', 'Change Password')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ bgcolor: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '14px', p: 2 }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: '#0369A1', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              🔒 {t('userProfile.passwordRequirements', 'Password Requirements:')}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {[
                t('userProfile.passwordReqLength', 'At least 8 characters'),
                t('userProfile.passwordReqUppercase', 'At least one uppercase letter (A-Z)'),
                t('userProfile.passwordReqLowercase', 'At least one lowercase letter (a-z)'),
                t('userProfile.passwordReqNumber', 'At least one number (0-9)'),
                t('userProfile.passwordReqSpecial', 'At least one special character (!@#$%^&*)'),
              ].map((req, i) => (
                <Box component="li" key={i} sx={{ color: '#0C4A6E', fontSize: '0.8rem', lineHeight: 1.6 }}>{req}</Box>
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1 }}>{t('userProfile.oldPassword', 'Old Password')}</Typography>
            <TextField type="password" placeholder="••••••••" value={oldPassword} onChange={e => setOldPassword(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1 }}>{t('userProfile.newPassword', 'New Password')}</Typography>
            <TextField type="password" placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1 }}>{t('userProfile.confirmPassword', 'Confirm New Password')}</Typography>
            <TextField type="password" placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F9FAFB' } }} />
          </Box>
          {passwordError && (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>
              {passwordError === 'كلمة المرور لا تستوفي متطلبات الأمان.' ? t('userProfile.passwordSecurityError', 'كلمة المرور لا تستوفي متطلبات الأمان.') : passwordError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setPasswordDialogOpen(false)} disabled={isChangingPassword} sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold', bgcolor: '#F3F4F6', color: '#374151', '&:hover': { bgcolor: '#E5E7EB' } }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handlePasswordChangeSubmit} disabled={isChangingPassword} variant="contained" sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold', bgcolor: '#000', '&:hover': { bgcolor: '#1F2937' } }}>
            {isChangingPassword ? <CircularProgress size={22} color="inherit" /> : t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Logout Confirm Dialog ─── */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 2, textAlign: 'center' } }}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 3 }}>
          <Avatar sx={{ bgcolor: '#FEF2F2', width: 72, height: 72 }}>
            <LogoutIcon sx={{ color: '#EF4444', fontSize: 36 }} />
          </Avatar>
          <Typography variant="h6" fontWeight="bold">{t('userProfile.logOut', 'Log Out')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('userProfile.logoutConfirm', 'Are you sure you want to log out?')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold', bgcolor: '#F3F4F6', color: '#374151', '&:hover': { bgcolor: '#E5E7EB' } }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleLogout} variant="contained" color="error" sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold' }}>
            {t('userProfile.logOut', 'Log Out')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Account Dialog ─── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 2, textAlign: 'center' } }}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 3 }}>
          <Avatar sx={{ bgcolor: '#FEE2E2', width: 80, height: 80 }}>
            <WarningAmberIcon sx={{ color: '#EF4444', fontSize: 48 }} />
          </Avatar>
          <Typography variant="h6" fontWeight="bold">{t('userProfile.deleteAccount', 'Delete Account')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('userProfile.deleteWarning', 'This action cannot be undone. All your data will be permanently deleted.')}
          </Typography>
          {deleteError && <Alert severity="error" sx={{ borderRadius: '12px', width: '100%' }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold', bgcolor: '#F3F4F6', color: '#374151', '&:hover': { bgcolor: '#E5E7EB' } }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleDeleteAccount} disabled={deleting} variant="contained" color="error" sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold' }}>
            {deleting ? <CircularProgress size={22} color="inherit" /> : t('userProfile.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── ✅ NEW: City Selection Dialog ─── */}
      <Dialog open={cityDialogOpen} onClose={() => setCityDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 0 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">{t('userProfile.selectCity', 'Select City')}</Typography>
          <IconButton onClick={() => setCityDialogOpen(false)} sx={{ bgcolor: '#F3F4F6', width: 36, height: 36 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, maxHeight: '60vh' }}>
          {cities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <BusinessIcon sx={{ fontSize: 64, color: '#E5E7EB', mb: 2 }} />
              <Typography color="text.secondary">{t('userProfile.noCities', 'No cities available')}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {cities.map((city) => {
                const cityName = city.Name || city.name || '';
                const isSelected = (user?.City || user?.city || '').toLowerCase() === cityName.toLowerCase();
                return (
                  <Box
                    key={city.Id || city.id}
                    onClick={() => handleChangeCity(city)}
                    sx={{
                      display: 'flex', alignItems: 'center', p: 2, borderRadius: '14px', cursor: 'pointer',
                      bgcolor: isSelected ? '#F0FDF4' : '#F9FAFB',
                      border: isSelected ? '1.5px solid #10B981' : '1.5px solid #E5E7EB',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: isSelected ? '#ECFDF5' : '#F3F4F6', borderColor: isSelected ? '#10B981' : '#D1D5DB' }
                    }}
                  >
                    <Avatar sx={{ bgcolor: isSelected ? '#D1FAE5' : '#F3F4F6', width: 40, height: 40, mr: 1.5 }}>
                      <BusinessIcon sx={{ color: isSelected ? '#059669' : '#6B7280', fontSize: 20 }} />
                    </Avatar>
                    <Typography sx={{ flex: 1, fontWeight: isSelected ? 700 : 600, fontSize: '1rem', color: isSelected ? '#059669' : '#111827' }}>
                      {cityName}
                    </Typography>
                    {isSelected && <CheckCircleIcon sx={{ color: '#10B981', fontSize: 24 }} />}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default UserProfile;